import noop from 'lodash/noop'
import Paper from 'paper'
import { distinctUntilChanged, map, takeWhile } from 'rxjs/operators'

import { Coordinate } from '../../../../../../models/activeDrawable'
import { DRAWING_TYPES, PAPER_ELEMENT_TYPE } from '../../../../../../shared/constants/drawable-types'
import { KeyNames } from '../../../../../../shared/constants/key-names'
import { RootState } from '../../../../../../stores'
import { calculateAreaFromCoords } from '../../../../../../utils/calculations/areaCalculation/calculateAreaFromCoords'
import {
    applyScaleFactorToPathLength,
    convertScaleFactorLabelEnumToDecimal,
    parseScaleFactorLabel,
} from '../../../../../../utils/calculations/scaleConversion/scaleConversion'
import { BulkUpdateOpeningLocationCoordinatesPayload } from '../../../../../sagas/2D/bulkUpdateOpeningLocationCoordinates'
import { initial2DState } from '../../../../../slices/2D'
import { determineActiveDocumentChunkId, NormalizedDocumentChunk } from '../../../../../slices/documents'
import { initialToolsState } from '../../../../../slices/tools'
import {
    Cursors,
    IMUP2DCoordinatesToUpdate,
    IMUP2DDrawableCutout,
    IMUPState,
    IStateMediator,
    ItemScale,
    ITool,
    MeasurementsToUpdate,
    MouseButtonCodes,
    MouseButtonsCodes,
    PaperToolConfig,
    REGION_ENUMS,
    VIEW_MODE,
} from '../../../../../types'

/**
 *  PaperTool.ts
 *
 *  Interface definition for Paper.js tools
 *  Handles interfacing with state and initial tool setup
 *  More functionality is added in Base tool subclasses depending on tool requirements
 */
export class PaperTool extends Paper.Tool implements ITool {
    protected mediator: IStateMediator
    protected paper: typeof Paper
    protected measureStrokeColor = 'red'
    protected measureStrokeWidth = 2
    protected defaultCrosshairMarkerSize = 100
    protected activeMappingId: IMUPState['mappings']['activeMappingId'] = null
    protected projectDocumentMappings: IMUPState['documents']['projectDocumentMappings'] = null
    protected scaleFactor: IMUPState['2D']['scaleFactor'] = initial2DState.scaleFactor
    protected documentChunks: IMUPState['documents']['documentChunks'] = null
    protected activeFloorDocId: IMUPState['documents']['activeFloorDocId'] = null
    protected activeDocumentChunkId: IMUPState['documents']['activeDocumentChunkId'] = null
    protected selectedItemId: IMUPState['2D']['selectedItem'] = initial2DState.selectedItem
    protected keyboardMultiSelectedItems: IMUPState['2D']['keyboardMultiSelectedItems'] =
        initial2DState.keyboardMultiSelectedItems
    protected activeGeometryGroupID: IMUPState['geometry']['activeGeometryGroupID'] = null
    protected strokeWidth: IMUPState['tools']['strokeWidth'] = initialToolsState.strokeWidth
    protected calibrationRatio: IMUPState['tools']['calibrationRatio'] = initialToolsState.calibrationRatio
    protected snappingEnabled: IMUPState['tools']['snappingEnabled'] = initialToolsState.snappingEnabled
    protected isSnappingTemporaryDisabled: IMUPState['tools']['isSnappingTemporaryDisabled'] =
        initialToolsState.isSnappingTemporaryDisabled
    protected maxImageDimension: IMUPState['2D']['maxDimension'] = initial2DState.maxDimension
    protected selectedAiSuggestionIds: IMUPState['2D']['selectedAiSuggestionIds'] =
        initial2DState.selectedAiSuggestionIds
    protected activeFlag: IMUPState['materialFlags']['activeFlag'] = null

    protected panLastScreenX: number | null = null
    protected panLastScreenY: number | null = null

    private currentOverlayChunkId: number | null = null
    private currentEstimatedOptionOverlayChunkId: number | null = null
    private isConflictResolutionComplete = false
    private panResetListener: ((e: MouseEvent) => void) | null = null
    private multiSelectedItemIds: number[] = []

    private static ARROW_KEY_DELTA = 1
    private static DEGREE_INCREMENT = 45
    private static readonly HANDLE_MAX_SIZE_IN_PX = 75

    public currentActiveCursor: string | null = null

    /**
     * Translates original coordinates to match the current bounds position.
     * Used for rounded paths (like highlights) where we need to persist the original
     * rectangle coordinates but apply the movement delta from multi-select operations.
     */
    public static translateOriginalCoordinatesToCurrentBounds(
        originalCoordinates: number[][],
        currentBounds: { x: number; y: number }
    ): Coordinate[] {
        const xs = originalCoordinates.map((c) => c[0])
        const ys = originalCoordinates.map((c) => c[1])
        const deltaX = currentBounds.x - Math.min(...xs)
        const deltaY = currentBounds.y - Math.min(...ys)

        return originalCoordinates.map((coord): Coordinate => [coord[0] + deltaX, coord[1] + deltaY])
    }

    private snappingItems: paper.Item[] = []

    name?: string
    cursor: string = Cursors.AUTO

    constructor(config: PaperToolConfig) {
        super()
        this.paper = config.paper
        this.mediator = config.mediator

        this.mediator
            .get$()
            .pipe(
                takeWhile(
                    (state: IMUPState) =>
                        state.common.activeMode === VIEW_MODE.Markup2D ||
                        state.common.activeMode === VIEW_MODE.Markup2DFor3D
                ),
                map((state: IMUPState) => ({
                    measureStrokeColor: state.tools.measureStrokeColor,
                    measureStrokeWidth: state.tools.measureStrokeWidth,
                    crosshairMarkerSize: state.tools.crosshairMarkerSize,
                    scaleFactor: state['2D'].scaleFactor,
                    activeFloorDocId: state.documents.activeFloorDocId,
                    activeDocumentChunkId: state.documents.activeDocumentChunkId,
                    documentChunks: state.documents.documentChunks,
                    selectedItem: state['2D'].selectedItem,
                    keyboardMultiSelectedItems: state['2D'].keyboardMultiSelectedItems,
                    activeGeometryGroupID: state.geometry.activeGeometryGroupID,
                    strokeWidth: state['tools'].strokeWidth,
                    activeMappingId: state.mappings.activeMappingId,
                    projectDocumentMappings: state.documents.projectDocumentMappings,
                    calibrationRatio: state.tools.calibrationRatio,
                    snappingEnabled: state.tools.snappingEnabled,
                    isSnappingTemporaryDisabled: state.tools.isSnappingTemporaryDisabled,
                    maxDimension: state['2D'].maxDimension,
                    stateCursor: state.common.cursor,
                    multiSelectedItemIds: state['2D'].multiSelectedDrawableIds,
                    selectedAiSuggestionIds: state['2D'].selectedAiSuggestionIds,
                    activeFlag: state.materialFlags.activeFlag,
                    currentOverlayChunkId: state['2D'].currentOverlayChunkIds?.length
                        ? state['2D'].currentOverlayChunkIds[state['2D'].currentOverlayChunkIds.length - 1]
                        : null,
                    currentEstimatedOptionOverlayChunkId: state['2D'].currentEstimatedOptionOverlayChunkIds?.length
                        ? state['2D'].currentEstimatedOptionOverlayChunkIds[
                              state['2D'].currentEstimatedOptionOverlayChunkIds.length - 1
                          ]
                        : null,
                    isConflictResolutionComplete: state.masterSetPlan.isConflictResolutionComplete,
                })),
                distinctUntilChanged()
            )
            .subscribe(
                ({
                    measureStrokeColor,
                    measureStrokeWidth,
                    crosshairMarkerSize,
                    scaleFactor,
                    activeFloorDocId,
                    activeDocumentChunkId,
                    documentChunks,
                    selectedItem,
                    keyboardMultiSelectedItems,
                    activeGeometryGroupID,
                    strokeWidth,
                    activeMappingId,
                    projectDocumentMappings,
                    calibrationRatio,
                    snappingEnabled,
                    maxDimension,
                    isSnappingTemporaryDisabled,
                    stateCursor,
                    multiSelectedItemIds,
                    selectedAiSuggestionIds,
                    activeFlag,
                    currentOverlayChunkId,
                    currentEstimatedOptionOverlayChunkId,
                    isConflictResolutionComplete,
                }) => {
                    this.measureStrokeColor = measureStrokeColor
                    this.measureStrokeWidth = measureStrokeWidth
                    this.defaultCrosshairMarkerSize = crosshairMarkerSize
                    this.scaleFactor = scaleFactor
                    this.activeFloorDocId = activeFloorDocId
                    this.activeDocumentChunkId = activeDocumentChunkId
                    this.documentChunks = documentChunks
                    this.selectedItemId = selectedItem
                    this.keyboardMultiSelectedItems = keyboardMultiSelectedItems
                    this.activeGeometryGroupID = activeGeometryGroupID
                    this.strokeWidth = strokeWidth
                    this.activeMappingId = activeMappingId
                    this.projectDocumentMappings = projectDocumentMappings
                    this.calibrationRatio = calibrationRatio
                    this.snappingEnabled = snappingEnabled
                    this.maxImageDimension = maxDimension
                    this.isSnappingTemporaryDisabled = isSnappingTemporaryDisabled
                    this.currentActiveCursor = stateCursor
                    this.multiSelectedItemIds = multiSelectedItemIds
                    this.selectedAiSuggestionIds = selectedAiSuggestionIds
                    this.activeFlag = activeFlag
                    this.currentOverlayChunkId = currentOverlayChunkId
                    this.currentEstimatedOptionOverlayChunkId = currentEstimatedOptionOverlayChunkId
                    this.isConflictResolutionComplete = isConflictResolutionComplete
                }
            )
    }

    protected setSnappingItems = (clickedItem?: paper.Item) => {
        const items = this.paper.project.getItems({
            data: (data) => (data?.drawable_id || data?.aiSuggestion?.id) && !data?.toolName,
            id: (id: number) => id !== clickedItem?.id,
            visible: true,
        })

        this.snappingItems = items.reduce((paths: paper.Path[], item) => {
            if (item?.children?.length > 0) {
                item.children.forEach((child) => {
                    if (child.className === 'Path') {
                        paths.push(child as paper.Path)
                    }
                })
            } else if (item.className === 'Path') {
                paths.push(item as paper.Path)
            }

            return paths
        }, [])
    }

    protected getSnappingItems = () => {
        return this.snappingItems
    }

    /**
     * Determines the target radius at a given zoom level
     */
    protected calculateHandleRadiusAtCurrentZoom = (): number => {
        const sizeAtCurZoom = (this.maxImageDimension * ItemScale.HANDLE) / this.paper.view.zoom

        return sizeAtCurZoom < PaperTool.HANDLE_MAX_SIZE_IN_PX ? sizeAtCurZoom : PaperTool.HANDLE_MAX_SIZE_IN_PX
    }

    /**
     * Intended to be used on the `mouseDown` event to determine if the click should be used as a pan click or a normal click
     * @param event
     * @returns boolean -> true if it is a panning click
     */
    protected isPanningClick = (event: paper.ToolEvent): boolean => {
        // indicate pan is active on right click
        const clickCode = event['event']['button']

        if ([MouseButtonCodes.Right, MouseButtonCodes.Middle].includes(clickCode)) {
            this.setState('common', { cursor: Cursors.GRAB })

            return true
        }

        return false
    }

    /**
     * Converts a PathItem to the expected payload for the API
     * @param item
     * @param scaleFactor
     * @returns Obj {
     *       coordinates, -> core coordinates of the item (in the case of a compound path, this is the first item in the CompoundPath segment list)
     *       measurements, -> the appropriate dimension (length or area) of the PathItem scaled from path coordinates to IRL coordinatess
     *       cutouts, -> in the case of a compound path, this is an array of type IMUP2DDrawableCutout otherwise it's undefined
     *    }
     */
    public convertPaperItemToAPICoordinateModel = (item: paper.Item): IMUP2DCoordinatesToUpdate => {
        let coordinates: Coordinate[] = []
        let cutouts: IMUP2DDrawableCutout[] | undefined = undefined
        const measurements: MeasurementsToUpdate = {
            quantity: null,
            linear_total: null,
            area: null,
        }

        // When item is a paper.Path, we will have either a single list of a single coordinate for a POINT type
        // or we will have a list of coordinates that represent the points in a line
        // For a compound path, we will have a list of the lists of coordinates that represent the entire path with all child segments
        if (item instanceof this.paper.Path) {
            if (item.data.shapeType === DRAWING_TYPES.POINT) {
                coordinates = [[item.position.x, item.position.y]] // since a point will only be one coordinate (segments of a point will return the circle)
            } else if (item.data.originalCoordinates) {
                // Update originalCoordinates from current segments to capture any added vertices
                item.data.originalCoordinates = item.segments.map((s) => [s.point.x, s.point.y])
                coordinates = PaperTool.translateOriginalCoordinatesToCurrentBounds(
                    item.data.originalCoordinates,
                    item.bounds
                )
            } else {
                coordinates = item.segments.map((segment) => [segment.point.x, segment.point.y])
            }

            if (item.data.shapeType === DRAWING_TYPES.SECTION || item.data.shapeType === DRAWING_TYPES.RADIUS) {
                measurements.linear_total = applyScaleFactorToPathLength({
                    pxValue: item.length,
                    scaleFactor: this.scaleFactor,
                    dpi: this.getActiveDocumentChunk()?.dpi ?? null,
                    xCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_x ?? 1,
                    yCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_y ?? 1,
                    coordinates: coordinates,
                    pdfScale: this.getActiveDocumentChunk()?.pdf_scale ?? 1,
                })
                measurements.quantity = measurements.linear_total
            } else if (item.data.shapeType === DRAWING_TYPES.AREA) {
                measurements.quantity = calculateAreaFromCoords({
                    coords: coordinates.flatMap((coord) => coord),
                    scaleFactor: this.scaleFactor,
                    dpi: this.getActiveDocumentChunk()?.dpi ?? null,
                    xCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_x ?? 1,
                    yCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_y ?? 1,
                    pdfScale: this.getActiveDocumentChunk()?.pdf_scale ?? 1,
                })
                measurements.area = measurements.quantity
                measurements.linear_total = applyScaleFactorToPathLength({
                    pxValue: item.length,
                    scaleFactor: this.scaleFactor,
                    dpi: this.getActiveDocumentChunk()?.dpi ?? null,
                    xCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_x ?? 1,
                    yCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_y ?? 1,
                    coordinates: coordinates,
                    pdfScale: this.getActiveDocumentChunk()?.pdf_scale ?? 1,
                })
            }
        } else if (item instanceof this.paper.CompoundPath) {
            // The outside path will be the primary set of coordinates to update
            const outsidePath = item.children[0] as paper.Path

            coordinates = outsidePath.segments.map((segment) => {
                return [segment.point.x, segment.point.y]
            })

            // All child paths of the CompoundPath will be treated as cutouts
            const cutoutPaths = item.children.slice(1) as paper.Path[]

            cutouts = cutoutPaths.map((path) => {
                return {
                    coordinates: path.segments.map((segment) => [segment.point.x, segment.point.y]),
                }
            })

            if (item.data.shapeType === DRAWING_TYPES.SECTION) {
                measurements.linear_total = applyScaleFactorToPathLength({
                    pxValue: item.length,
                    scaleFactor: this.scaleFactor,
                    dpi: this.getActiveDocumentChunk()?.dpi ?? null,
                    xCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_x ?? 1,
                    yCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_y ?? 1,
                    coordinates: coordinates,
                    pdfScale: this.getActiveDocumentChunk()?.pdf_scale ?? 1,
                })
                measurements.quantity = measurements.linear_total
            } else if (item.data.shapeType === DRAWING_TYPES.AREA) {
                const convertAreaCoordsToSquareFeet = (areaCoords: Coordinate[]): number =>
                    calculateAreaFromCoords({
                        coords: areaCoords.flatMap((coord) => coord),
                        scaleFactor: this.scaleFactor,
                        dpi: this.getActiveDocumentChunk()?.dpi ?? null,
                        xCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_x ?? 1,
                        yCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_y ?? 1,
                        pdfScale: this.getActiveDocumentChunk()?.pdf_scale ?? 1,
                    })

                measurements.quantity =
                    convertAreaCoordsToSquareFeet(coordinates) -
                    cutouts.reduce((total, cutout) => total + convertAreaCoordsToSquareFeet(cutout.coordinates), 0)
                measurements.area = measurements.quantity
                measurements.linear_total = applyScaleFactorToPathLength({
                    pxValue: item.length,
                    scaleFactor: this.scaleFactor,
                    dpi: this.getActiveDocumentChunk()?.dpi ?? null,
                    xCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_x ?? 1,
                    yCalibrationFactor: this.getActiveDocumentChunk()?.calibration_factor_y ?? 1,
                    coordinates: coordinates,
                    pdfScale: this.getActiveDocumentChunk()?.pdf_scale ?? 1,
                })
            }
        }

        return {
            coordinates,
            measurements,
            cutouts,
        }
    }

    /**
     * Intended to be used in the `onMouseDrag` event to perform the pan within a tool
     * @param event
     * @returns boolean -> true if is a panning drag
     */
    protected toolPanning = (event: paper.ToolEvent): boolean => {
        const nativeEvent = event['event'] as MouseEvent
        const clickCode = nativeEvent.buttons

        // for panning on right click & middle click drag (bitwise check to handle simultaneous left + right/middle)
        if (clickCode & (MouseButtonsCodes.Right | MouseButtonsCodes.Middle)) {
            this.setState('common', { cursor: Cursors.GRABBING })

            // Use native screen coordinates for delta calculation instead of paper.js project
            // coordinates. Project coordinates shift when view.center changes, causing the view
            // to rubber-band back to the original point. Screen coordinates are view-independent.
            if (this.panLastScreenX === null || this.panLastScreenY === null) {
                this.panLastScreenX = nativeEvent.clientX
                this.panLastScreenY = nativeEvent.clientY
            } else {
                const dx = this.panLastScreenX - nativeEvent.clientX
                const dy = this.panLastScreenY - nativeEvent.clientY

                // Convert screen-pixel delta to project-space delta using the current zoom level
                const zoom = this.paper.view.zoom

                this.paper.view.center = this.paper.view.center.add(new this.paper.Point(dx / zoom, dy / zoom))

                this.panLastScreenX = nativeEvent.clientX
                this.panLastScreenY = nativeEvent.clientY
            }

            // Register a one-time document mouseup listener to reset pan state when the
            // right/middle button is released. This prevents stale screen coordinates from
            // causing a view jump on the next pan session across ALL tools.
            if (!this.panResetListener) {
                this.panResetListener = (e: MouseEvent) => {
                    if (e.button === MouseButtonCodes.Right || e.button === MouseButtonCodes.Middle) {
                        this.panLastScreenX = null
                        this.panLastScreenY = null

                        document.removeEventListener('mouseup', this.panResetListener!)
                        this.panResetListener = null
                    }
                }

                document.addEventListener('mouseup', this.panResetListener)
            }

            return true
        }

        this.panLastScreenX = null
        this.panLastScreenY = null

        return false
    }

    protected getActiveDocumentChunk = (): NormalizedDocumentChunk | null => {
        if (this.documentChunks) {
            const searchId = this.activeDocumentChunkId
                ? this.activeDocumentChunkId
                : determineActiveDocumentChunkId(
                      this.activeFloorDocId,
                      this.currentOverlayChunkId,
                      this.currentEstimatedOptionOverlayChunkId,
                      this.isConflictResolutionComplete
                  )
            const searchResult = this.documentChunks.find((chunk) => chunk.id === searchId)

            return searchResult !== undefined ? searchResult : null
        }

        return null
    }

    /**
     * Snap what the user is drawing to the nearest 45 deg to help draw straight lines
     * @param originPoint the point to measure the angle against
     * @param proposedNewPoint the mouse position used to determine the angle and the nearest 45
     * @returns point on the nearest 45
     */
    protected calculateFixedAnglePoint = (originPoint: paper.Point, proposedNewPoint: paper.Point): paper.Point => {
        const xDiff = proposedNewPoint.x - originPoint.x
        const yDiff = proposedNewPoint.y - originPoint.y
        const baseAngle = new this.paper.Point(xDiff, yDiff).angle
        const magnitude = Math.sqrt(Math.pow(xDiff, 2) + Math.pow(yDiff, 2))
        const newPoint = new this.paper.Point({
            length: magnitude,
            angle: Math.round(baseAngle / PaperTool.DEGREE_INCREMENT) * PaperTool.DEGREE_INCREMENT,
        })

        return newPoint.add(originPoint)
    }

    /**
     * Set the current active scale factor according to either the region or document mapping of the input point
     * @param point the sample point in a region, or document mapping if there is no region
     */
    protected setScaleFromPointClick = (point: paper.Point): number | undefined => {
        // Find the region we are drawing in, so we can set the scale if necessary
        const regionPaths = this.paper.project.getItems({
            data: {
                shapeType: REGION_ENUMS.TYPE,
            },
        })

        const thisRegion = regionPaths.find((r) => r.contains(point))

        // set the region id if point is in region
        this.setState('2D', { regionId: thisRegion?.data?.region_id || null })

        // If this point is in a region, set the scale of that region, otherwise take it from the document mapping
        if (thisRegion && thisRegion.data.scale && thisRegion.data.scale !== '') {
            const scaleFactor = parseScaleFactorLabel(thisRegion.data.scale) ?? 0

            this.setState('2D', { scaleFactor })

            return scaleFactor
        }

        if (this.projectDocumentMappings) {
            const activeMapping = this.projectDocumentMappings.find(
                (mapping) =>
                    (!!this.activeMappingId && mapping.id === this.activeMappingId) ||
                    (!this.activeMappingId && mapping.document_chunk_id === this.activeFloorDocId)
            )

            if (activeMapping) {
                const scaleFactor = convertScaleFactorLabelEnumToDecimal(activeMapping.scale_factor)

                this.setState('2D', { scaleFactor })

                return scaleFactor
            }
        }
    }

    protected activateBaseOnKeyUpActions = (keyEvent): void => {
        // activate snapping if snapping was deactivated by hotkey
        if (keyEvent.key === KeyNames.CONTROL && this.isSnappingTemporaryDisabled && !this.snappingEnabled) {
            this.mediator.mediate('tools', { snappingEnabled: true, isSnappingTemporaryDisabled: false })
        }

        if (keyEvent.key === 'escape') {
            // If we hit the escape key, deactivate the tool
            this.cancel()
        }

        const isDeleteKey = ['delete', 'backspace'].includes(keyEvent.key)
        const target = keyEvent.event?.target
        const isFormElement =
            target &&
            (['input', 'textarea'].includes(target.tagName?.toLowerCase() || '') ||
                target.classList?.contains('ql-editor'))

        if (!isDeleteKey || isFormElement || this.isDeletionBlocked()) return

        // Keep multi-selections, AI suggestions, and flags enabled when hitting delete
        const hasMultiSelections =
            this.multiSelectedItemIds.length > 0 ||
            this.selectedAiSuggestionIds.length > 0 ||
            // when a material group is active (e.g. selected in the materials panel) the panel handles Delete itself
            ((this.keyboardMultiSelectedItems?.length ?? 0) > 0 && this.activeGeometryGroupID === null)

        // Don't enable delete function for drawables
        const isDrawable = this.isSelectedItemDrawable()

        // The delete order of precedence and the fact that
        // we do not at present (as of Q4 2026) purge
        // selected item id on form submit means that the
        // selected item id will be updated and as such multi
        // selections will not work see: https://portal.myparadigm.com/browse/BLUEPRINTS-24183
        // to prevent this override the check to make sure that
        // we can always delete multiselections
        if (!hasMultiSelections && isDrawable) return

        // Allow deletion for all non-drawable items
        if (hasMultiSelections || this.activeFlag || this.selectedItemId) {
            // Show deletion modal
            this.mediator.mediate('tools', { deletionModalVisible: true })
        }
    }

    activate = () => {
        super.activate()
        this.mediator.mediate('tools', { activeTool: this.name })
        this.onActivate()
    }

    onActivate = () => {}

    /**
     * onDeactivate()
     * Cancels the current action of the tool and sets the activeTool state to nothing
     */
    onDeactivate = () => {
        this.cancel()
        this.mediator.mediate('tools', { activeTool: '' })
    }

    /**
     * cancel()
     * Cancels the current action of the tool (i.e. remove the shape being drawn or cleanup non drawable paper items)
     * Does NOT deactivate the tool in state
     * Should be implemented in the specific tools when necessary -- no cancellation to be done in Base tool.
     */
    cancel = () => noop()

    /**
     * handle delete
     * @param keyEvent
     */
    onKeyUp = (keyEvent) => {
        this.activateBaseOnKeyUpActions(keyEvent)
    }

    onKeyDown = (keyEvent) => {
        // deactivate snapping when snapping is enabled and hotkey is pressed
        if (keyEvent.key === KeyNames.CONTROL && this.snappingEnabled) {
            this.mediator.mediate('tools', { snappingEnabled: false, isSnappingTemporaryDisabled: true })
        }
    }

    setState = <K extends keyof RootState['IMUP'], V extends Partial<RootState['IMUP'][K]>>(key: K, value: V): this => {
        this.mediator.mediate(key, value)

        return this
    }

    getPaperScope = (): paper.PaperScope => this.paper

    /**
     * Generates a group that marks the point being measured
     * @param point Center point for the marker
     * @param length
     * @param color
     */
    constructCrosshairMarker = (point: paper.Point, length?: number, color?: paper.Color): paper.Group => {
        const elements: paper.Path[] = []
        const size = length ?? this.defaultCrosshairMarkerSize

        elements.push(
            new this.paper.Path.Line(
                new this.paper.Point(point.x - size, point.y),
                new this.paper.Point(point.x + size, point.y)
            )
        )
        elements.push(
            new this.paper.Path.Line(
                new this.paper.Point(point.x, point.y - size),
                new this.paper.Point(point.x, point.y + size)
            )
        )

        // apply styling to each line
        elements.forEach((line) => {
            line.style.strokeColor = color ? color : new this.paper.Color(0, 1, 0)
            line.strokeWidth = this.measureStrokeWidth
            line.strokeScaling = false
            line.locked = true
        })

        return new this.paper.Group(elements)
    }

    /**
     * Applies a rounded effect to corners of a paper path in place
     * @param path The path to round
     * @param radius The radius to apply to each vertex
     * @returns The rounded path item (will not be a new instance of a paper.Path)
     */
    public roundPath = (path: paper.Path, radius: number) => {
        // only store original coordinates once
        if (!path.data.originalCoordinates) {
            path.data.originalCoordinates = path.segments.map((s) => [s.point.x, s.point.y])
        }

        const segments = path.segments.slice(0)

        path.removeSegments()

        for (let i = 0, l = segments.length; i < l; i++) {
            const curPoint = segments[i].point
            const nextPoint = segments[i + 1 === l ? 0 : i + 1].point
            const prevPoint = segments[i - 1 < 0 ? segments.length - 1 : i - 1].point
            const nextDelta = curPoint.subtract(nextPoint)
            const prevDelta = curPoint.subtract(prevPoint)

            nextDelta.length = radius
            prevDelta.length = radius

            path.add(new this.paper.Segment(curPoint.subtract(prevDelta), undefined, prevDelta.divide(2)))

            path.add(new this.paper.Segment(curPoint.subtract(nextDelta), nextDelta.divide(2), undefined))
        }

        path.closed = true

        return path
    }

    /**
     * Undoes a rounded effect generated by roundPath to corners of a paper path in place
     * @param path The path to de-round
     * @returns The de-rounded path item (will not be a new instance of a paper.Path)
     */
    public deroundPath = (path: paper.Path) => {
        if (!path.data.originalCoordinates) return path

        path.removeSegments()

        for (const coord of path.data.originalCoordinates ?? []) {
            path.add(new this.paper.Point(coord[0], coord[1]))
        }

        path.closed = true

        // clear the cached original coordinates to avoid conflicts
        path.data.originalCoordinates = null

        return path
    }

    protected getHighestImageRasterIndex = () => {
        const rasters = this.paper.project.activeLayer.getItems({
            class: this.paper.Raster,
        })

        return Math.max(...rasters.map((raster) => raster.index))
    }

    getItemsWithCriteria = (key: string, criteria: (data: any) => boolean): paper.Item[] => {
        return this.paper.project.getItems({
            [key]: criteria,
        })
    }

    protected sendItemOnlyOnTopOfMainRaster = (item: paper.Item) => {
        const rasters = this.getItemsWithCriteria('data', (data) => data.isMainRaster === true)

        if (rasters.length !== 1) {
            this.paper.project.activeLayer.insertChild(0, item)
        } else {
            this.paper.project.activeLayer.insertChild(rasters[0].index + 1, item)
        }
    }

    public sendItemToBackBeforeRaster = (item: paper.Item): void => {
        const highestImageIndex = this.getHighestImageRasterIndex()

        this.paper.project.activeLayer.insertChild(highestImageIndex + 1, item)
    }

    public convertGroupChildrenToAPIUpdateData = (group: paper.Group) => {
        return group.children.reduce((data, child) => {
            const dataToUpdate = this.convertPaperItemToAPICoordinateModel(child)

            if (dataToUpdate.coordinates.length > 0) {
                data.push({
                    coordinates: dataToUpdate.coordinates,
                    cutouts: dataToUpdate.cutouts,
                    measurements: dataToUpdate.measurements,
                    opening_group_id: child.data.opening_group_id,
                    opening_id: child.data.drawable_id,
                    opening_location_id: child.data.opening_location_id,
                })
            }

            return data
        }, [] as BulkUpdateOpeningLocationCoordinatesPayload)
    }

    private mirrorItem = ({
        item,
        translationValue,
        returnTranslationValue,
        scalingValues,
    }: {
        item: paper.Item
        translationValue: paper.Point
        returnTranslationValue: paper.Point
        scalingValues: [number, number]
    }) => {
        item.translate(translationValue)
        item.scale(scalingValues[0], scalingValues[1])
        item.translate(returnTranslationValue)
    }

    public mirrorItemVertically = (item: paper.Item) => {
        const translationValue = new this.paper.Point(-item.bounds.rightCenter.x, 0)
        const returnTranslationValue = new this.paper.Point(item.bounds.rightCenter.x, 0)
        const scalingValues: [number, number] = [1, -1]

        this.mirrorItem({
            item,
            translationValue,
            returnTranslationValue,
            scalingValues,
        })
    }

    public mirrorItemHorizontally = (item: paper.Item) => {
        const translationValue = new this.paper.Point(0, -item.bounds.center.y)
        const returnTranslationValue = new this.paper.Point(0, item.bounds.center.y)
        const scalingValues: [number, number] = [-1, 1]

        this.mirrorItem({
            item,
            translationValue,
            returnTranslationValue,
            scalingValues,
        })
    }

    /**
     * Takes in the shape to be styled and applies the styles from the parent
     * @param shape -> shape to be styled
     * @param parent -> shape to copy styles from
     * @returns
     */
    protected styleNewCompoundPathShape = (shape: paper.CompoundPath, referencePath: paper.Item) => {
        const color: paper.Color = referencePath.fillColor ? referencePath.fillColor : referencePath.strokeColor!

        color.alpha = 0.8 // set the opacity value for areas

        shape.strokeColor = color
        shape.strokeWidth = this.scaleFactor * ItemScale.LINE // scale the stroke width to be 4" no matter the resolution (1/3 is used because the scale is feet : canvasUnit)
        shape.fillColor = color
        shape.fillRule = 'evenodd' // this makes it so the children of the compound path are cutout instead of being additive
        shape.strokeWidth = 1 // hide since shape is filled
        shape.children[0].data = null

        return shape
    }

    protected determinePointToTranslate(direction: string) {
        switch (direction) {
            case KeyNames.ARROW_UP:
                return new this.paper.Point(0, -PaperTool.ARROW_KEY_DELTA)
            case KeyNames.ARROW_LEFT:
                return new this.paper.Point(-PaperTool.ARROW_KEY_DELTA, 0)
            case KeyNames.ARROW_DOWN:
                return new this.paper.Point(0, PaperTool.ARROW_KEY_DELTA)
            case KeyNames.ARROW_RIGHT:
                return new this.paper.Point(PaperTool.ARROW_KEY_DELTA, 0)
            default:
                return new this.paper.Point(0, 0)
        }
    }

    /**
     * Tools may override this to handle delete/backspace themselves
     * (e.g. pen removing its last vertex) instead of opening the deletion modal
     */
    protected isDeletionBlocked(): boolean {
        return false
    }

    /**
     * Checks if the selected item is a Drawable
     * @returns boolean indicating if the selected item is drawable
     */
    protected isSelectedItemDrawable(): boolean {
        if (!this.selectedItemId) {
            return false
        }

        const item = this.paper.project.getItem({ id: this.selectedItemId })

        if (!item) {
            return false
        }

        return item.data?.type === PAPER_ELEMENT_TYPE.DRAWABLE
    }
}

export default PaperTool
